import { t } from '@lingui/core/macro';
import {
    QuoteRequestDTO,
    QuoteRequestFieldName,
    QuoteRequestFieldNameRuntype,
    QuoteRequestStatus,
} from '@luminovo/api-contracts';
import { assertUnreachable, chainComparators, compareByNumber, compareByStringKey } from '@luminovo/commons';
import { formatList } from '@luminovo/frontend-utils';

import { formatQuoteRequestField } from '../formatters';
import { QuoteRequestFieldCategory, QuoteRequestFieldConfig } from '../types';

/**
 * A small immutable datastructure to manage quote request field and makes sure that the following invariants are maintained:
 *
 * 1. All fields are assigned to one of the three categories: required, hidden, optional
 * 2. No field is assigned to more than one category
 * 3. The optional fields are always inferred as the fields that are not required or hidden
 * 4. The always required fields are always in the required category
 */
export class QuoteRequestConfigurationFields {
    private _required: Set<QuoteRequestFieldName>;
    private _hidden: Set<QuoteRequestFieldName>;
    private _allFields = QuoteRequestFieldNameRuntype.options;
    private _optional: Set<QuoteRequestFieldName>;

    constructor({ required, hidden }: { required?: QuoteRequestFieldName[]; hidden?: QuoteRequestFieldName[] }) {
        // Start by setting the required fields
        this._required = new Set(required || []);

        // Then set the hidden fields
        this._hidden = new Set(hidden || []);
        // In case the user tried to hide a required field, remove it from the hidden set
        this._required.forEach((field) => this._hidden.delete(field));

        // Then calculate the optional fields
        this._optional = new Set(
            this._allFields.filter((field) => !this._required.has(field) && !this._hidden.has(field)),
        );
    }

    static fromQuoteRequest(quoteRequest?: QuoteRequestDTO): QuoteRequestConfigurationFields {
        if (!quoteRequest) {
            return new QuoteRequestConfigurationFields({});
        }
        return new QuoteRequestConfigurationFields({
            required: quoteRequest.configuration?.required_fields ?? [],
            hidden: quoteRequest.configuration?.hidden_fields ?? [],
        });
    }

    getFormField(field: QuoteRequestFieldName): FormFieldConfiguration {
        const fieldInfo = QuoteRequestFieldRegistry[field];
        // Override all settings if the field is always required
        if (fieldInfo.config === QuoteRequestFieldConfig.ALWAYS_REQUIRED) {
            return {
                required: true,
                visible: true,
            };
        }
        if (this._hidden.has(field)) {
            return {
                required: false,
                visible: false,
            };
        }
        if (this._required.has(field)) {
            return {
                required: true,
                visible: true,
            };
        }
        return {
            required: false,
            visible: true,
        };
    }

    withRequired(required: QuoteRequestFieldName[]): QuoteRequestConfigurationFields {
        return new QuoteRequestConfigurationFields({
            required,
            hidden: Array.from(this._hidden).filter((field) => !required.includes(field)),
        });
    }

    withHidden(hidden: QuoteRequestFieldName[]): QuoteRequestConfigurationFields {
        const filteredRequired = Array.from(this._required).filter((field) => !hidden.includes(field));

        return new QuoteRequestConfigurationFields({
            required: filteredRequired,
            hidden,
        });
    }

    alsoHide(hidden: QuoteRequestFieldName[]): QuoteRequestConfigurationFields {
        const newHidden = this.hidden.concat(hidden);
        const newRequired = this.required.filter((field) => !hidden.includes(field));
        return new QuoteRequestConfigurationFields({
            required: newRequired,
            hidden: newHidden,
        });
    }

    hideByCategory(...categories: QuoteRequestFieldCategory[]): QuoteRequestConfigurationFields {
        return this.alsoHide(
            this._allFields.filter((field) => categories.includes(QuoteRequestFieldRegistry[field].category)),
        );
    }

    withOptional(optional: QuoteRequestFieldName[]): QuoteRequestConfigurationFields {
        return new QuoteRequestConfigurationFields({
            required: Array.from(this._required).filter((field) => !optional.includes(field)),
            hidden: Array.from(this._hidden).filter((field) => !optional.includes(field)),
        });
    }

    get required(): ReadonlyArray<QuoteRequestFieldName> {
        return Array.from(this._required).sort();
    }

    get hidden(): ReadonlyArray<QuoteRequestFieldName> {
        return Array.from(this._hidden).sort();
    }

    get optional(): ReadonlyArray<QuoteRequestFieldName> {
        return Array.from(this._optional).sort();
    }

    get all(): ReadonlyArray<QuoteRequestFieldName> {
        return this._allFields;
    }

    formatRequired(): string {
        if (this.required.length === 0) {
            return '-';
        }
        return formatList(
            this.required.map((field) => formatQuoteRequestField(field)),
            'en',
        );
    }

    formatHidden(): string {
        if (this.hidden.length === 0) {
            return '-';
        }
        return formatList(
            this.hidden.map((field) => formatQuoteRequestField(field)),
            'en',
        );
    }

    /**
     * There are 3 possible formats:
     * 1. When there are no hidden fields. Render as "default".
     * 2. If there is at least one hidden field and no changes to the mandatory required fields
     *    "A,B C hidden"
     * 3. If there is at least one hidden field and changes to the mandatory required fields
     *    "A,B, C required and D,E,F hidden"
     */
    format(): string {
        if (this.hidden.length === 0 && this.required.length === 0) {
            return t`All fields optional`;
        }

        const listHidden = this.formatHidden();
        const listMandatory = this.formatRequired();

        if (this.required.length === 0) {
            return t`Hidden: ${listHidden}`;
        }

        return t`Required: ${listMandatory}; Hidden: ${listHidden}`;
    }

    /**
     * Depending on the contents of the quote requests, some fields are not relevant.
     * For example, if the quote requests are off-the-shelf, the "Attachment" field is not relevant.
     */
    toggleFieldsByContents(partType: 'mixed' | 'off-the-shelf' | 'custom'): QuoteRequestConfigurationFields {
        if (partType === 'mixed') {
            // no changes for mixed quote requests
            return this;
        }
        if (partType === 'off-the-shelf') {
            return this.alsoHide(['Attachment']);
        }
        if (partType === 'custom') {
            return this.alsoHide([
                // Request fields
                'RequiredQuantity',
                'Unit',
                'PotentialQuantity',
                'TargetPrice',
                'Customer',
                'DeliveryLotSize',

                'Packaging',
                'Ncnr',
                'PartDescription',
                'IpnDescription',
                'OfferedMpn',

                'Ipn',
                'CancellationTimeUnit',
                'CancellationWindow',
                'EarlyPaymentDiscount',
                'EarlyPaymentDiscountDays',
                'Recipient',
                'NetPaymentDueDays',
                'ItemClass',
                'RequestedMpn',
                'RequestedManufacturer',
                'Stock',

                // Part fields
                'Reach',
                'Rohs',
                'EccnNumbers',
                'HtsCode',
                'CountryOfOrigin',
            ]);
        }
        assertUnreachable(partType);
    }

    toggleFieldsForViewMode(status: QuoteRequestStatus): QuoteRequestConfigurationFields {
        switch (status) {
            case QuoteRequestStatus.NotSent:
            case QuoteRequestStatus.Sent:
            case QuoteRequestStatus.Overdue:
                return this.hideByCategory(
                    QuoteRequestFieldCategory.OFFER_INFORMATION,
                    QuoteRequestFieldCategory.PART_INFORMATION,
                ).alsoHide([
                    // Table fields
                    'RowStatus',
                    'Bid',
                ]);

            case QuoteRequestStatus.Received:
            case QuoteRequestStatus.Discarded:
                // hide part fields
                return this.hideByCategory(QuoteRequestFieldCategory.PART_INFORMATION).alsoHide([
                    // Table fields
                    'RowStatus',
                    'Bid',
                ]);
        }
    }

    /**
     * We will eventually get rid of this method and just use the QuoteRequestConfigurationFields.getFormField() directly.
     */
    getFormFieldConfigurations(): FormFieldsConfiguration {
        return {
            AdditionalNotes: this.getFormField('AdditionalNotes'),
            Attachment: this.getFormField('Attachment'),
            Bid: this.getFormField('Bid'),
            CancellationTimeUnit: this.getFormField('CancellationTimeUnit'),
            CancellationWindow: this.getFormField('CancellationWindow'),
            CountryOfOrigin: this.getFormField('CountryOfOrigin'),
            Customer: this.getFormField('Customer'),
            PartDescription: this.getFormField('PartDescription'),
            IpnDescription: this.getFormField('IpnDescription'),
            EccnNumbers: this.getFormField('EccnNumbers'),
            HtsCode: this.getFormField('HtsCode'),
            Ipn: this.getFormField('Ipn'),
            ItemClass: this.getFormField('ItemClass'),
            LeadTime: this.getFormField('LeadTime'),
            LeadTimeUnit: this.getFormField('LeadTimeUnit'),
            RequestedManufacturer: this.getFormField('RequestedManufacturer'),
            MinimumOrderQuantity: this.getFormField('MinimumOrderQuantity'),
            MinimumPackagingQuantity: this.getFormField('MinimumPackagingQuantity'),
            RequestedMpn: this.getFormField('RequestedMpn'),
            Ncnr: this.getFormField('Ncnr'),
            NegotiatedByCustomer: this.getFormField('NegotiatedByCustomer'),
            OfferedManufacturer: this.getFormField('OfferedManufacturer'),
            OfferedMpn: this.getFormField('OfferedMpn'),
            OneTimeCost: this.getFormField('OneTimeCost'),
            PartCategory: this.getFormField('PartCategory'),
            Packaging: this.getFormField('Packaging'),
            PotentialQuantity: this.getFormField('PotentialQuantity'),
            DeliveryLotSize: this.getFormField('DeliveryLotSize'),
            Reach: this.getFormField('Reach'),
            Recipient: this.getFormField('Recipient'),
            RequiredQuantity: this.getFormField('RequiredQuantity'),
            Rohs: this.getFormField('Rohs'),
            Stock: this.getFormField('Stock'),
            SupplierPartNumber: this.getFormField('SupplierPartNumber'),
            TargetPrice: this.getFormField('TargetPrice'),
            Unit: this.getFormField('Unit'),
            UnitPrice: this.getFormField('UnitPrice'),
            UnitPriceCurrency: this.getFormField('UnitPriceCurrency'),
            UnitPriceQuantity: this.getFormField('UnitPriceQuantity'),
            ValidFor: this.getFormField('ValidFor'),
            ValidFrom: this.getFormField('ValidFrom'),
            ValidUntil: this.getFormField('ValidUntil'),
            QuoteRequestLineItemStatus: this.getFormField('QuoteRequestLineItemStatus'),
            EarlyPaymentDiscount: this.getFormField('EarlyPaymentDiscount'),
            EarlyPaymentDiscountDays: this.getFormField('EarlyPaymentDiscountDays'),
            NetPaymentDueDays: this.getFormField('NetPaymentDueDays'),
            RowStatus: this.getFormField('RowStatus'),
            TotalOfferedQuantity: this.getFormField('TotalOfferedQuantity'),
            BomCpn: this.getFormField('BomCpn'),
            BomIpn: this.getFormField('BomIpn'),
            IpnCpn: this.getFormField('IpnCpn'),
        };
    }

    toFormState(): QuoteRequestField[] {
        // Both are frontend-only fields.
        const fields = this.all
            .filter((field) => field !== 'QuoteRequestLineItemStatus' && field !== 'RowStatus')
            .map((field) => {
                const fieldInfo = QuoteRequestFieldRegistry[field];
                const isRequired = this._required.has(field);
                const isHidden = this._hidden.has(field);
                const isAlwaysRequired = fieldInfo.config === QuoteRequestFieldConfig.ALWAYS_REQUIRED;

                let config: QuoteRequestFieldConfig;
                if (isAlwaysRequired) {
                    config = QuoteRequestFieldConfig.ALWAYS_REQUIRED;
                } else if (isRequired) {
                    config = QuoteRequestFieldConfig.REQUIRED;
                } else if (isHidden) {
                    config = QuoteRequestFieldConfig.HIDDEN;
                } else {
                    config = QuoteRequestFieldConfig.OPTIONAL;
                }

                return {
                    id: field,
                    name: fieldInfo.name(),
                    desciption: fieldInfo.description(),
                    category: fieldInfo.category,
                    config,
                };
            });

        return fields.sort(
            chainComparators(
                compareByNumber((field: QuoteRequestField) => {
                    const order = {
                        [QuoteRequestFieldConfig.REQUIRED]: 0,
                        [QuoteRequestFieldConfig.OPTIONAL]: 1,
                        [QuoteRequestFieldConfig.HIDDEN]: 2,
                        [QuoteRequestFieldConfig.ALWAYS_REQUIRED]: 3,
                    };
                    return order[field.config];
                }),
                compareByNumber((field: QuoteRequestField) => {
                    const order = {
                        [QuoteRequestFieldCategory.OFFER_INFORMATION]: 0,
                        [QuoteRequestFieldCategory.QUOTE_REQUEST]: 1,
                        [QuoteRequestFieldCategory.PART_INFORMATION]: 2,
                    };
                    return order[field.category];
                }),
                compareByStringKey((field: QuoteRequestField) => field.name),
            ),
        );
    }
}

/**
 * @deprecated replace witn `QuoteRequestConfigurationFields`
 */
export type FormFieldsConfiguration = {
    [K in QuoteRequestFieldName]: FormFieldConfiguration;
};

export type FormFieldConfiguration = {
    visible: boolean;
    required?: boolean;
};

export type QuoteRequestField = {
    id: QuoteRequestFieldName;
    name: string;
    desciption: string;
    category: QuoteRequestFieldCategory;
    config: QuoteRequestFieldConfig;
};

type RegistryEntry = {
    id: QuoteRequestFieldName;
    name: () => string;
    description: () => string;
    /**
     * Determines to which category this field belongs to.
     */
    category: QuoteRequestFieldCategory;
    /**
     * Determins to which kinds of parts this field is relevant.
     */
    partCategory: 'mixed' | 'off-the-shelf' | 'custom';
    config?: QuoteRequestFieldConfig.ALWAYS_REQUIRED;

    // context:
    // - user type
    // - view/edit mode
    // - status of the quote request (has offers or no offers)
    // - initial quote request configuration
    // - content of the quote request line items (mixed, off-the-shelf, custom)
    //
    // Principle: every field is visible by default, unless overridden by the context
    // The context can only hide fields, it cannot make them required/not-required
    //
    // 1. (Context object) => QuoteRequestFieldConfig
    // 2. "user-is-admin", "view-mode","has-offers"
};

export const QuoteRequestFieldRegistry: Record<QuoteRequestFieldName, RegistryEntry> = {
    AdditionalNotes: {
        id: 'AdditionalNotes',
        name: () => t`Additional notes`,
        description: () => t`Can be used to leave a comment about the offer.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    Attachment: {
        id: 'Attachment',
        name: () => t`Attachment`,
        description: () => t`Attach relevant documents to the quote request`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'custom',
    },
    Bid: {
        id: 'Bid',
        name: () => t`Bid`,
        description: () => t`Indicates if a pricing will be provided at a later time.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    CancellationTimeUnit: {
        id: 'CancellationTimeUnit',
        name: () => t`Cancellation time unit`,
        description: () => t`Time unit for cancellation window`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'custom',
    },
    CancellationWindow: {
        id: 'CancellationWindow',
        name: () => t`Cancellation window`,
        description: () => t`Specifies the time window in which the order can be cancelled.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'custom',
    },
    Customer: {
        id: 'Customer',
        name: () => t`End customer`,
        description: () => t`End customer information`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    IpnDescription: {
        id: 'IpnDescription',
        name: () => t`Description(IPN)`,
        description: () => t`Description of the IPN if available.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    PartDescription: {
        id: 'PartDescription',
        name: () => t`Description(Part)`,
        description: () => t`Description of the requested part.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    PartCategory: {
        id: 'PartCategory',
        name: () => t`Part category`,
        description: () => t`Part category of the requested part`,
        category: QuoteRequestFieldCategory.PART_INFORMATION,
        partCategory: 'mixed',
    },
    Ipn: {
        id: 'Ipn',
        name: () => t`IPN`,
        description: () => t`Internal part number from the buyer's ERP.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'off-the-shelf',
    },
    ItemClass: {
        id: 'ItemClass',
        name: () => t`Item class`,
        description: () => t`Classification of the item`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    LeadTime: {
        id: 'LeadTime',
        name: () => t`Lead time`,
        description: () =>
            t`If you don't have anything in stock and nothing on-order, the lead time to restock from the manufacturer.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    LeadTimeUnit: {
        id: 'LeadTimeUnit',
        name: () => t`Std. factory lead time unit`,
        description: () => t`Unit for the Lead Time until the part can be shipped.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    RequestedManufacturer: {
        id: 'RequestedManufacturer',
        name: () => t`Requested manufacturer`,
        description: () => t`Manufacturer of the requested part.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    MinimumOrderQuantity: {
        id: 'MinimumOrderQuantity',
        name: () => t`Minimum order quantity`,
        description: () => t`Minimum quantity that can be ordered`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    MinimumPackagingQuantity: {
        id: 'MinimumPackagingQuantity',
        name: () => t`Minimum packaging quantity`,
        description: () => t`Orders must be in multiples of this value.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    RequestedMpn: {
        id: 'RequestedMpn',
        name: () => t`Requested MPN`,
        description: () => t`Manufacturer part number of the requested part.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    Ncnr: {
        id: 'Ncnr',
        name: () => t`NCNR`,
        description: () => t`Is a purchase order non-cancellable and non-returnable`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    NegotiatedByCustomer: {
        id: 'NegotiatedByCustomer',
        name: () => t`Negotiated by customer`,
        description: () => t`Whether the price was negotiated by the customer`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    OfferedManufacturer: {
        id: 'OfferedManufacturer',
        name: () => t`Offered manufacturer`,
        description: () => t`Manufacturer of the offered part.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    OfferedMpn: {
        id: 'OfferedMpn',
        name: () => t`Offered MPN`,
        description: () => t`MPN of the offered part.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    OneTimeCost: {
        id: 'OneTimeCost',
        name: () => t`One-time cost`,
        description: () => t`One-time cost.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    Packaging: {
        id: 'Packaging',
        name: () => t`Packaging`,
        description: () => t`Packaging option.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    PotentialQuantity: {
        id: 'PotentialQuantity',
        name: () => t`Potential quantity`,
        description: () => t`Total quantity across all quote request line items where this part is approved.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    DeliveryLotSize: {
        id: 'DeliveryLotSize',
        name: () => t`Delivery lot size`,
        description: () => t`The delivery lot size indicates the number of units in each delivery.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    Recipient: {
        id: 'Recipient',
        name: () => t`Ship to`,
        description: () => t`The site the parts will be shipped to.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    RequiredQuantity: {
        id: 'RequiredQuantity',
        name: () => t`Required quantity`,
        description: () => t`The amount required for a requested part.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    Stock: {
        id: 'Stock',
        name: () => t`Stock`,
        description: () => t`Number of units in stock / available to sell.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    SupplierPartNumber: {
        id: 'SupplierPartNumber',
        name: () => t`Supplier part number`,
        description: () => t`Supplier part number of the offered part.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    TargetPrice: {
        id: 'TargetPrice',
        name: () => t`Target price`,
        description: () => t`Target price for the requested part.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    Unit: {
        id: 'Unit',
        name: () => t`Unit`,
        description: () => t`Unit of measurement of the offer.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    UnitPrice: {
        id: 'UnitPrice',
        name: () => t`Unit price`,
        description: () => t`Price per unit based on the unit price quantity.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    UnitPriceCurrency: {
        id: 'UnitPriceCurrency',
        name: () => t`Currency`,
        description: () => t`The currency code for the unit price following the ISO 4217 standard.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    UnitPriceQuantity: {
        id: 'UnitPriceQuantity',
        name: () => t`Unit Price Quantity`,
        description: () => t`Determines the quantity of units specified by the given Unit Price.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
        config: QuoteRequestFieldConfig.ALWAYS_REQUIRED,
    },
    ValidFor: {
        id: 'ValidFor',
        name: () => t`Offer valid for`,
        description: () => t`Specifies if the offer is valid for a customer or a project.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    ValidFrom: {
        id: 'ValidFrom',
        name: () => t`Price valid from`,
        description: () => t`Specifies from when the offered price is valid.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    ValidUntil: {
        id: 'ValidUntil',
        name: () => t`Price valid until`,
        description: () => t`Specifies until when the offered price is valid.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    CountryOfOrigin: {
        id: 'CountryOfOrigin',
        name: () => t`Country of origin`,
        description: () => t`Country of origin of the part according to the UN M49 standard.`,
        category: QuoteRequestFieldCategory.PART_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    EccnNumbers: {
        id: 'EccnNumbers',
        name: () => t`ECCN numbers`,
        description: () => t`ECCN numbers.`,
        category: QuoteRequestFieldCategory.PART_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    HtsCode: {
        id: 'HtsCode',
        name: () => t`HTS code`,
        description: () => t`HTS code.`,
        category: QuoteRequestFieldCategory.PART_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    Reach: {
        id: 'Reach',
        name: () => t`REACH`,
        description: () => t`REACH compliance status.`,
        category: QuoteRequestFieldCategory.PART_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    Rohs: {
        id: 'Rohs',
        name: () => t`RoHS`,
        description: () => t`RoHS compliance status.`,
        category: QuoteRequestFieldCategory.PART_INFORMATION,
        partCategory: 'off-the-shelf',
    },
    QuoteRequestLineItemStatus: {
        id: 'QuoteRequestLineItemStatus',
        name: () => t`Quote request line item status`,
        description: () => t`Status of the quote request line item.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    RowStatus: {
        id: 'RowStatus',
        name: () => t`Row status`,
        description: () => t`Status of the row.`,
        category: QuoteRequestFieldCategory.QUOTE_REQUEST,
        partCategory: 'mixed',
    },
    EarlyPaymentDiscount: {
        id: 'EarlyPaymentDiscount',
        name: () => t`Early payment discount %`,
        description: () => t`The discount by which the unit price is reduced if the invoice is paid early. E.g. 10%`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    EarlyPaymentDiscountDays: {
        id: 'EarlyPaymentDiscountDays',
        name: () => t`Early payment discount days`,
        description: () =>
            t`If paid before this number of days, the unit price is reduced by the Early Payment Discount.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    NetPaymentDueDays: {
        id: 'NetPaymentDueDays',
        name: () => t`Net payment due days`,
        description: () =>
            t`The number of days in which the invoice must be paid in full, relative to the offer’s valid from date.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    TotalOfferedQuantity: {
        id: 'TotalOfferedQuantity',
        name: () => t`Total offered quantity`,
        description: () => t`Total quantity offered.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'custom',
    },
    BomCpn: {
        id: 'BomCpn',
        name: () => t`Customer part numbers from BOM`,
        description: () => t`Customer part numbers from the BOM.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    BomIpn: {
        id: 'BomIpn',
        name: () => t`IPN from BOM`,
        description: () => t`Internal part number from the BOM.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
    IpnCpn: {
        id: 'IpnCpn',
        name: () => t`Customer part numbers`,
        description: () => t`Customer part numbers linked to the IPN.`,
        category: QuoteRequestFieldCategory.OFFER_INFORMATION,
        partCategory: 'mixed',
    },
};
